// Elaborazione automatica del logo caricato dall'utente.
// Genera le versioni necessarie a partire da un'immagine (PNG/JPEG/...):
//   - original: copia ridotta dell'originale (a colori) usata per ri-scalare
//   - display:  versione a colori per anteprima e scontrino HTML
//   - print:    versione dedicata alla stampa termica (scala di grigi, contrasto
//               ottimizzato, sfondo bianco, larghezza scelta dall'utente)
//
// La versione "print" viene salvata in `logo_print_base64` e usata dalla stampa
// ePOS-Print: avere un formato dedicato garantisce dimensioni e resa costanti.

/** Larghezza stampabile in punti (dot) per la carta scelta. */
export function printableWidth(paperWidth) {
  return Number(paperWidth) === 58 ? 384 : 576;
}

/** Carica un'immagine da data URL/URL come Promise<HTMLImageElement>. */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Impossibile caricare il logo'));
    img.src = src;
  });
}

/**
 * Disegna l'immagine ridimensionata (mantenendo le proporzioni) su un canvas
 * e ritorna { dataUrl, width, height }.
 */
function renderResized(img, maxW, maxH, grayscale, contrast = 1.0) {
  const sw = img.naturalWidth || img.width;
  const sh = img.naturalHeight || img.height;
  const scale = Math.min(maxW / sw, maxH / sh, 1); // non ingrandire oltre l'originale
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement('canvas');
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // sfondo bianco (gestione trasparenza)
  ctx.fillRect(0, 0, dw, dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, dw, dh);

  if (grayscale) {
    const imgData = ctx.getImageData(0, 0, dw, dh);
    const px = imgData.data;
    for (let i = 0; i < px.length; i += 4) {
      let lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      lum = (lum - 128) * contrast + 128; // contrasto attorno al grigio medio
      lum = Math.max(0, Math.min(255, lum));
      px[i] = px[i + 1] = px[i + 2] = lum;
      px[i + 3] = 255;
    }
    ctx.putImageData(imgData, 0, 0);
  }

  return { dataUrl: canvas.toDataURL('image/png'), width: dw, height: dh };
}

/** Rifila i bordi bianchi/trasparenti, restituendo un canvas tagliato sul contenuto. */
function trimToContent(img) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  let data;
  try { data = ctx.getImageData(0, 0, w, h).data; } catch { return c; }
  let top = h, left = w, right = -1, bottom = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const a = data[i + 3];
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (a > 16 && lum < 245) {
        if (x < left) left = x; if (x > right) right = x;
        if (y < top) top = y; if (y > bottom) bottom = y;
      }
    }
  }
  if (right < left || bottom < top) return c; // immagine vuota: nessun trim
  const pad = Math.round(Math.max(w, h) * 0.01);
  left = Math.max(0, left - pad); top = Math.max(0, top - pad);
  right = Math.min(w - 1, right + pad); bottom = Math.min(h - 1, bottom + pad);
  const cw = right - left + 1, ch = bottom - top + 1;
  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  out.getContext('2d').drawImage(c, left, top, cw, ch, 0, 0, cw, ch);
  return out;
}

/** Copia ridotta dell'originale (a colori, rifilata) usata per ri-scalare in seguito. */
export async function prepareOriginal(dataUrl, maxSide = 1200) {
  const img = await loadImage(dataUrl);
  const trimmed = trimToContent(img);
  const scale = Math.min(maxSide / trimmed.width, maxSide / trimmed.height, 1);
  const dw = Math.max(1, Math.round(trimmed.width * scale));
  const dh = Math.max(1, Math.round(trimmed.height * scale));
  const out = document.createElement('canvas');
  out.width = dw; out.height = dh;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dw, dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(trimmed, 0, 0, dw, dh);
  return out.toDataURL('image/png');
}

/** Versione a colori per anteprima/HTML. */
export async function buildDisplayLogo(dataUrl, maxW = 360) {
  const img = await loadImage(dataUrl);
  return renderResized(img, maxW, maxW, false).dataUrl;
}

/**
 * Versione dedicata alla stampa termica, alla larghezza richiesta (in punti).
 * @returns {Promise<{dataUrl:string, width:number, height:number}>}
 */
export async function buildPrintLogo(dataUrl, printWidth) {
  const img = await loadImage(dataUrl);
  const w = Math.max(64, Math.round(printWidth || 384));
  // Altezza massima generosa: per i loghi (più larghi che alti) comanda la larghezza.
  return renderResized(img, w, 900, true, 1.25);
}

/**
 * Elabora un logo appena caricato.
 * @returns {Promise<{original, display, print, printWidth, printableMax}>}
 */
export async function processLogoUpload(dataUrl, opts = {}) {
  const paperWidth = Number(opts.paperWidth) === 58 ? 58 : 80;
  const printableMax = printableWidth(paperWidth);
  const printWidth = opts.printWidth || Math.round(printableMax * 0.75); // default 75%
  const original = await prepareOriginal(dataUrl);
  const display = await buildDisplayLogo(original);
  const print = (await buildPrintLogo(original, printWidth)).dataUrl;
  return { original, display, print, printWidth, printableMax };
}
